import { FC, useMemo, useState } from 'react'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import { LineChart, ChartsXAxisProps, ChartsTextStyle } from '@mui/x-charts'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'

interface RankHistoryEntry {
  date: string
  rankGroup: number | null
  rankAiOverview: number | null
}

interface TrackingHistoryTrendChartProps {
  data: RankHistoryEntry[]
  options?: {
    showAiOverview?: boolean
  }
}

const TrackingHistoryTrendChart: FC<TrackingHistoryTrendChartProps> = ({ data, options = {} }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false)

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const chartRenderData = useMemo(() => {
    if (!data || data.length < 1) {
      return null;
    }

    const allPoints = data.flatMap(item => [item.rankGroup, item.rankAiOverview]);
    const validPoints = allPoints.filter((p): p is number => p !== null && p > 0);

    let originalYAxisMin, originalYAxisMax;
    if (validPoints.length === 0) {
      originalYAxisMin = 0;
      originalYAxisMax = 100;
    } else {
      const dataMin = Math.min(...validPoints);
      const dataMax = Math.max(...validPoints);
      originalYAxisMin = dataMin > 5 ? dataMin - 5 : 0;
      originalYAxisMax = dataMax + 5;
    }

    const yAxisTop = originalYAxisMax;
    const transformY = (y: number | null) => (y === null ? null : yAxisTop - y);

    const transformedSortedData = data.map(item => ({
      date: item.date,
      rankGroup: transformY(item.rankGroup),
      rankAiOverview: item.rankAiOverview===-1?undefined: transformY(item.rankAiOverview)
    }));

    const dates = data.map(item => item.date);
    let tickValues = dates;

    // Crear mapa de colores por fecha basado en el índice del array de fechas
    const colorsByDateIndex = new Map<number, string>();

    const xAxisData: ChartsXAxisProps[] = [{
      data: dates,
      scaleType: 'point' as const,
      valueFormatter: (value: string, context) => {
        const [year, month, day] = value.split('-');
        return context.location === 'tick' ? `${day}/${month}` : `${day}/${month}/${year}`;
      },
      tickValues: tickValues,
      tickLabelStyle: {
        angle: -45,
        textAnchor: 'end',
      } as ChartsTextStyle
    }];

    const allSeries = [];

    // AI Overview serie
    if (options.showAiOverview) {
      allSeries.push({
        data: transformedSortedData.map(item => item.rankAiOverview),
        id: 'ai-overview',
        label: t('headerName.ai'),
        color: '#2196f3',
        showMark: false,
        connectNulls: true,
        area: true,
        valueFormatter: (val: number | null) => val !== null ? `${yAxisTop - val}` : ''
      });
    }

    const hasRankGroupData = transformedSortedData.some(d => d.rankGroup !== null);

    // Color segments y mapear colores por índice de fecha
    if (hasRankGroupData) {
      for (let i = 1; i < transformedSortedData.length; i++) {
        const prev = transformedSortedData[i - 1];
        const curr = transformedSortedData[i];
        const originalPrev = data[i - 1];
        const originalCurr = data[i];
        let color = '#BBBCBD';

        if (prev.rankGroup != null && curr.rankGroup != null && originalPrev.rankGroup !== null && originalCurr.rankGroup !== null) {
          if (originalCurr.rankGroup > originalPrev.rankGroup) color = '#4caf50';
          if (originalCurr.rankGroup < originalPrev.rankGroup) color = '#f44336';

          // Guardar colores para ambos índices del segmento
          colorsByDateIndex.set(i-1, color);
          colorsByDateIndex.set(i, color);

          const segmentData = Array(transformedSortedData.length).fill(null);
          segmentData[i-1] = prev.rankGroup;
          segmentData[i] = curr.rankGroup;

          allSeries.push({
            data: segmentData,
            id: `segment-${i}`,
            color,
            showMark: false,
            connectNulls: true,
            area: true,
            valueFormatter: () => null
          });
        }
      }

      // Serie principal invisible para el tooltip
      allSeries.push({
        data: transformedSortedData.map(item => item.rankGroup),
        id: 'position-main',
        label: t('headerName.position'),
        color: 'transparent',
        showMark: false,
        connectNulls: true,
        area: false,
        valueFormatter: (val: number | null) => val !== null ? `${yAxisTop - val}` : ''
      });
    }

    const yAxisConfig = {
      valueFormatter: (value: number) => Math.round(yAxisTop - value).toString(),
      min: 0,
      max: yAxisTop - originalYAxisMin,
    };

    return {
      xAxis: xAxisData,
      series: allSeries,
      yAxis: [yAxisConfig],
      originalData: data,
      yAxisTop,
      colorsByDateIndex,
      dates
    };
  }, [data, t, options?.showAiOverview]);

  if (!data || data.length < 2) {
    return (
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">{t('insufficientData')}</Typography>
      </Box>
    );
  }

  let rankLineColor = '#BBBCBD';
  if (data.length >= 2) {
    const lastItem = data[0];
    const previousItem = data[1];
    if (lastItem.rankGroup !== null && previousItem.rankGroup !== null) {
      if (lastItem.rankGroup < previousItem.rankGroup) rankLineColor = 'green';
      else if (lastItem.rankGroup > previousItem.rankGroup) rankLineColor = 'red';
    }
  }

  return (
    <>
      <Box onClick={handleClickOpen} sx={{ cursor: 'pointer' }}>
        <LineChart
          dataset={(data || [])
            .map(item => ({
              date: item.date,
              rankGroup: item.rankGroup === -1 ? undefined : item.rankGroup,
              rankAiOverview: item.rankAiOverview < 1 ? undefined : item.rankAiOverview
            }))
          }
          margin={{ top: 0, right: 0, bottom: 0, left: -60 }}
          hideLegend={true}
          slotProps={{
            axisLine: { style: { display: 'none' } },
            axisTick: { style: { display: 'none' } }
          }}
          xAxis={[{
            dataKey: 'date',
            scaleType: 'point',
            disableTicks: true,
            disableLine: true,
            tickLabelStyle: { display: 'none' },
            valueFormatter: (value: string) => {
              const [year, month, day] = value.split('-');
              return `${day}/${month}/${year}`;
            },
            reverse: true
          }]}
          yAxis={[{
            hideTooltip: true,
            disableTicks: true,
            disableLine: true,
            tickLabelStyle: { display: 'none' },
            reverse: true
          }]}
          series={[
            { dataKey: 'rankGroup', label: t('headerName.position'), color: rankLineColor, showMark: false },
            ...(options.showAiOverview ? [{ dataKey: 'rankAiOverview', label: t('headerName.ai'), color: '#6154E8', showMark: false }] : [])
          ]}
          width={180}
          height={60}
        />
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth disableScrollLock>
        <DialogTitle>
          {t('trackingHistoryTrend')}
          <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {chartRenderData ? (
            <Box sx={{ height: 400, width: '100%', pt: 2 }}>
              <LineChart
                dataset={(data || [])
                  .map(item => ({
                    date: item.date,
                    rankGroup: item.rankGroup === -1 ? undefined : item.rankGroup,
                    rankAiOverview: item.rankAiOverview < 1 ? undefined : item.rankAiOverview
                  }))
                }
                xAxis={[{
                  dataKey: 'date',
                  scaleType: 'point',
                  valueFormatter: (value: string, context) => {
                    const [year, month, day] = value.split('-');
                    return context.location === 'tick' ? `${day}/${month}` : `${day}/${month}/${year}`;
                  },
                  tickLabelStyle: {
                    angle: -45,
                    fontSize: 12
                  },
                  reverse: true
                }]}
                series={chartRenderData.series}
                yAxis={chartRenderData.yAxis}
                margin={{ top: 0, right: 20, bottom: 20, left: 0 }}
                hideLegend={true}
                sx={{
                  '.MuiAreaElement-root': {
                    fillOpacity: 0.5
                  },
                  '.MuiAreaElement-series-ai-overview': {
                    fillOpacity: 0.1
                  },
                  '.MuiChartsAxis-tickLabel': {
                    transformOrigin: 'end center',
                    textAnchor: 'end',
                    alignmentBaseline: 'middle'
                  }
                }}
              />
            </Box>
          ) : (
            <Typography>{t('noDataAvailable')}</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TrackingHistoryTrendChart;